import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { CSSProperties, useState } from 'react'

import { useRecordWisePipelinesStore } from '@flowwai-frontend/bps-officer-web/common/data-access'
import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'
import { PopOverPreferPlaces } from '@flowwai-frontend/shared/constants'

import PipelineItemPipelinesAPIController from '../../controllers/PipelineItemPipelinesAPIController/PipelineItemPipelinesAPIController'
import PopoverContent from '../PopoverContent'
import PipelinesSelectionPopoverBody from './PipelinesSelectionPopoverBody'

const popoverContainerStyles =
   'flex items-center rounded-[12px] min-h-[80px] min-w-[340px] shadow-xl'

interface Props {
   pipelineItemId: string
   popoverStyles?: CSSProperties
   popoverProps?: any
   onSelectPipeline: (pipelineId: string, onSuccessOpen?: () => void) => void
   onTogglePopover?: (isOpen: boolean) => void
   children: (onClickTrigger: () => void) => React.ReactElement
}

const PipelinesSelectionPopover = (props: Props): React.ReactElement => {
   const {
      pipelineItemId,
      popoverStyles,
      popoverProps,
      onTogglePopover,
      onSelectPipeline,
      children
   } = props

   const recordWisePipelinesStore = useRecordWisePipelinesStore()

   const { theme } = useAppTheme()
   const pipelinesPopoverTheme = theme.common.pipelinesPopover

   const [isPopoverOpen, setIsPopoverOpen] = useState(false)

   const openPopover = (): void => {
      setIsPopoverOpen(true)
      onTogglePopover?.(true)
   }

   const closePopover = (): void => {
      setIsPopoverOpen(false)
      onTogglePopover?.(false)
   }

   const onSelectItem = (pipelineId: string): void => {
      onSelectPipeline(pipelineId, closePopover)
   }

   const getFirstPipelineIfOnlyOneExists = (): string | null => {
      const hasPipelines = recordWisePipelinesStore.hasRecordPipelineIds(
         pipelineItemId
      )

      if (!hasPipelines) return null

      const pipelineIds = recordWisePipelinesStore.getRecordPipelineIds(
         pipelineItemId
      )
      const isOnlyOnePipelineExists = pipelineIds.length === 1

      return isOnlyOnePipelineExists ? pipelineIds[0] : null
   }

   const handlePopoverOpen = (): void => {
      if (isPopoverOpen) {
         closePopover()
         return
      }

      const firstPipelineId = getFirstPipelineIfOnlyOneExists()

      if (firstPipelineId) {
         onSelectPipeline(firstPipelineId)
      } else {
         openPopover()
      }
   }

   const renderPopoverBody = (): React.ReactElement => (
      <div
         className={cn(
            popoverContainerStyles,
            pipelinesPopoverTheme.bodyBg,
            pipelinesPopoverTheme.bodyBorder,
            'border border-solid'
         )}
      >
         <PipelineItemPipelinesAPIController pipelineItemId={pipelineItemId}>
            <PipelinesSelectionPopoverBody
               pipelineItemId={pipelineItemId}
               onSelect={onSelectItem}
            />
         </PipelineItemPipelinesAPIController>
      </div>
   )

   const popOverProps = {
      isOpen: isPopoverOpen,
      preferPlace: PopOverPreferPlaces.right,
      style: {
         zIndex: 10,
         width: '340px',
         ...popoverStyles
      },
      tipSize: 0.1,
      onOuterAction: closePopover,
      body: [renderPopoverBody()],
      offset: -100,
      ...popoverProps
   }

   return (
      <PopoverContent popoverProps={popOverProps}>
         {children(handlePopoverOpen)}
      </PopoverContent>
   )
}

export default observer(PipelinesSelectionPopover)
